// OWNER: LG-ADMIN. §10 tab 3: a row per search with status, the five stage bars (discovered, gated, enriched,
// classified, scored), spent vs budget and failed tasks; Pause / Resume / Cancel / Raise budget. Failed searches are
// red with their error. A row opens its detail (stage table, spend by vendor, errors, red lines). Active searches poll.
import { useEffect, useState } from 'preact/hooks';
import type { LeadSearch, SearchDetail } from '../../../src/shared/api-leadgen';
import { Empty, ErrorBanner, Modal, fmtDateTime, timeAgo, toast } from '../components/ui';
import { ApiErrorBox, Check, ConfirmModal, Field, FixedHead, inputValue, useLoad } from '../admin/ui';
import { apiErrorView, type ApiErrorView } from '../admin/errors';
import { leadgenApi } from './endpoints';
import {
  MODE_RULES, STATUS_LABELS, STATUS_TONES, areasSummary, failedTaskTotal, funnelLine, isActiveStatus, isPilotReport, isSmokeReport,
  progressBars, raiseProblem, searchActions, smokeSummary, usd, usd2, vendorLabel, type SearchAction,
} from './logic';
import { SEARCH_COLUMNS } from './layout';
import { Blockers, Meter, Money, StageBars, ToneChip, usePoll } from './parts';
import { useMe } from '../me';
import { navigate } from '../app';
import { useApprovals } from '../approvals';
import type { SearchApproval } from '../../../src/shared/api-leadgen';

/** SPEC §19: "Waiting for PJ's approval" / declined with the note, under the search's status. */
function ApprovalLine(props: { approval: SearchApproval | null | undefined; ownerName: string }) {
  const a = props.approval;
  if (!a) return null;
  const first = props.ownerName.split(' ')[0];
  if (a.status === 'pending') {
    return <span class="adm-cell-sub"><a href="/approvals" onClick={(e) => { e.preventDefault(); navigate('/approvals'); }}><ToneChip tone="warn">Waiting for {first}'s approval</ToneChip></a></span>;
  }
  if (a.status === 'declined') {
    return <span class="adm-cell-sub" style="white-space:normal"><ToneChip tone="bad">Declined by {a.decidedByName ?? first}</ToneChip>{a.note ? ` "${a.note}"` : ''}</span>;
  }
  if (a.status === 'approved') return <span class="adm-cell-sub">Approved by {a.decidedByName ?? first}{a.budgetUsd !== null ? ` (budget $${a.budgetUsd.toFixed(2)})` : ''}</span>;
  return <span class="adm-cell-sub">Request canceled</span>;
}

const PAGE = 25;

export function SearchesTab(props: { onOpenResults: (searchId: number) => void }) {
  const isAdmin = useMe().role === 'admin';
  const ownerName = useApprovals()?.ownerName ?? 'PJ';
  const [offset, setOffset] = useState(0);
  const list = useLoad(() => leadgenApi.searches(PAGE, offset), [offset]);
  const [details, setDetails] = useState<Record<number, SearchDetail>>({});
  const [detailErr, setDetailErr] = useState<Record<number, string>>({});
  const [open, setOpen] = useState<number | null>(null);
  const [raising, setRaising] = useState<LeadSearch | null>(null);
  const [canceling, setCanceling] = useState<LeadSearch | null>(null);

  const rows = list.data?.items ?? [];
  const anyActive = rows.some((s) => isActiveStatus(s.status));
  const loadDetail = async (id: number) => {
    try { const d = await leadgenApi.search(id); setDetails((m) => ({ ...m, [id]: d })); setDetailErr((m) => { const n = { ...m }; delete n[id]; return n; }); }
    catch (e) { setDetailErr((m) => ({ ...m, [id]: apiErrorView(e).message })); }
  };
  // stage bars need SearchDetail: fetch it for active searches and the open row only
  const wanted = rows.filter((s) => isActiveStatus(s.status) || s.id === open).map((s) => s.id);
  useEffect(() => { for (const id of wanted) void loadDetail(id); }, [wanted.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(() => { void list.reload(); for (const id of wanted) void loadDetail(id); }, 10000, anyActive);

  const act = async (s: LeadSearch, a: Exclude<SearchAction, 'raise' | 'cancel'>) => {
    try {
      const next = a === 'pause' ? await leadgenApi.pause(s.id) : await leadgenApi.resume(s.id);
      toast(`Search #${s.id}: ${STATUS_LABELS[next.status]}.`, 'ok');
      await list.reload(); void loadDetail(s.id);
    } catch (e) {
      const v = apiErrorView(e);
      toast(v.message, 'bad');
    }
  };

  return (
    <div class="stack" style="gap:12px">
      <div class="adm-toolbar">
        <span class="lg-note grow">{anyActive ? 'Refreshing every 10 s while a search is active.' : 'Click a row for its stages, spend by vendor and errors.'}</span>
        <button class="btn btn-sm" onClick={() => void list.reload()} disabled={list.loading}>Refresh</button>
      </div>
      <ErrorBanner error={list.error} />
      {list.loading && !list.data ? <div class="muted">Loading searches…</div> : null}
      {list.data && rows.length === 0 && offset === 0 ? <Empty>No searches yet. Start with a smoke test in New search.</Empty> : null}
      {rows.length ? (
        <div class="adm-table-wrap">
          <table class="adm-fixed">
            <FixedHead cols={SEARCH_COLUMNS} />
            <tbody>
              {rows.map((s) => {
                const d = details[s.id];
                const funnel = d?.funnel ?? s.funnel;
                const failed = failedTaskTotal(funnel);
                const isOpen = open === s.id;
                const actions = isAdmin ? searchActions(s.status) : [];
                return [
                  <tr class={`adm-clickable${s.status === 'failed' ? ' lg-row-bad' : ''}${isOpen ? ' lg-row-open' : ''}`}
                    onClick={(e) => { if (!(e.target as HTMLElement).closest('button,a')) setOpen(isOpen ? null : s.id); }} aria-expanded={isOpen}>
                    <td class="num">{s.id}</td>
                    <td><b>{s.nicheKey}</b> <span class="lg-mono">v{s.nicheVersion}</span><span class="adm-cell-sub">{MODE_RULES[s.mode as keyof typeof MODE_RULES]?.label ?? s.mode} · {fmtDateTime(s.createdAt)}</span></td>
                    <td>{areasSummary(s.areas)}<span class="adm-cell-sub">{s.country} · {s.terms.length} terms × {s.limitPerTerm}</span></td>
                    <td>
                      <ToneChip tone={STATUS_TONES[s.status]}>{STATUS_LABELS[s.status]}</ToneChip>
                      {s.stage && isActiveStatus(s.status) ? <span class="adm-cell-sub">at {s.stage}</span> : null}
                      {s.status === 'failed' && s.error ? <span class="adm-cell-sub lg-err-line" style="white-space:normal" title={s.error}>{s.error}</span> : null}
                      <ReportLine search={s} />
                      <ApprovalLine approval={s.approval} ownerName={ownerName} />
                    </td>
                    <td>
                      {d ? <StageBars bars={progressBars(d.stages, s)} /> : detailErr[s.id] ? <span class="lg-err-line">{detailErr[s.id]}</span>
                        : isActiveStatus(s.status) ? <span class="muted">loading…</span> : <span class="lg-note">{funnelLine(funnel)}</span>}
                    </td>
                    <td class="num">
                      <Money usd={s.spentUsd} /><span class="adm-cell-sub">of {usd2(s.budgetUsd)}</span>
                      <div style="margin-top:4px"><Meter value={s.budgetUsd > 0 ? s.spentUsd / s.budgetUsd : null} tone={s.status === 'paused_budget' ? 'over' : ''} label="Spent against budget" /></div>
                    </td>
                    <td class="num">{failed ? <ToneChip tone="bad" title={Object.entries(funnel?.failedTasks ?? {}).map(([k, n]) => `${k}: ${n}`).join(', ')}>{failed}</ToneChip> : '0'}</td>
                    <td>
                      <div class="stack" style="gap:4px;align-items:flex-start">
                        {s.status === 'review' || s.status === 'imported' || (funnel?.raw ?? 0) > 0 || s.report
                          ? <button class="btn btn-sm btn-ink" onClick={() => props.onOpenResults(s.id)}>{s.report && !(funnel?.raw ?? 0) ? 'Report' : 'Results'}</button> : null}
                        {actions.includes('pause') ? <button class="btn btn-sm" onClick={() => void act(s, 'pause')}>Pause</button> : null}
                        {actions.includes('resume') ? <button class="btn btn-sm" onClick={() => void act(s, 'resume')}>Resume</button> : null}
                        {actions.includes('raise') ? <button class="btn btn-sm" onClick={() => setRaising(s)}>Raise budget</button> : null}
                        {actions.includes('cancel') ? <button class="btn btn-sm" onClick={() => setCanceling(s)}>Cancel</button> : null}
                      </div>
                    </td>
                  </tr>,
                  isOpen ? (
                    <tr class="lg-detail"><td colSpan={SEARCH_COLUMNS.length}>
                      {d ? <SearchDetailView detail={d} /> : detailErr[s.id] ? <ErrorBanner error={detailErr[s.id]} /> : <span class="muted">Loading detail…</span>}
                    </td></tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {list.data && (offset > 0 || list.data.total > offset + PAGE) ? (
        <div class="row">
          <button class="btn btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Newer</button>
          <span class="lg-note">{offset + 1}–{offset + rows.length} of {list.data.total}</span>
          <button class="btn btn-sm" disabled={offset + PAGE >= list.data.total} onClick={() => setOffset(offset + PAGE)}>Older →</button>
        </div>
      ) : null}
      {raising ? <RaiseBudgetModal search={raising} onClose={() => setRaising(null)} onDone={() => { void list.reload(); void loadDetail(raising.id); }} /> : null}
      {canceling ? (
        <ConfirmModal title={`Cancel search #${canceling.id}?`} danger confirmLabel="Cancel the search"
          body={<>Queued work stops and nothing more is spent. Rows already found stay for review. Spent so far: {usd(canceling.spentUsd)}.</>}
          onConfirm={async () => { await leadgenApi.cancel(canceling.id); toast(`Search #${canceling.id} canceled.`, 'ok'); await list.reload(); }}
          onClose={() => setCanceling(null)} />
      ) : null}
    </div>
  );
}

/** Under the status: the smoke's pass count or the pilot's precision, so a failed smoke says why at a glance. */
function ReportLine(props: { search: LeadSearch }) {
  const r = props.search.report;
  if (isSmokeReport(r)) {
    const sum = smokeSummary(r);
    return <span class={`adm-cell-sub lg-report-line${sum.passed ? '' : ' lg-err-line'}`} title={sum.text}>smoke {sum.passedCount}/{sum.total} {sum.passed ? 'passed' : 'failed'}</span>;
  }
  if (isPilotReport(r)) {
    const p = r.inNiche.precision;
    return <span class="adm-cell-sub lg-report-line" title="In-niche precision on the pilot's hand checks">pilot precision {p === null ? 'not measured' : p.toFixed(2)}</span>;
  }
  return null;
}

function SearchDetailView(props: { detail: SearchDetail }) {
  const { detail: d } = props;
  return (
    <div class="stack" style="gap:12px">
      {d.redLines.length ? <Blockers items={d.redLines} /> : null}
      <div class="lg-grid-2">
        <div class="stack">
          <span class="eyebrow">Stages</span>
          <div class="adm-table-wrap">
            <table>
              <thead><tr><th>Stage</th><th class="num">Queued</th><th class="num">Running</th><th class="num">Waiting</th><th class="num">Done</th><th class="num">Failed</th></tr></thead>
              <tbody>
                {d.stages.map((st) => (
                  <tr class={d.search.stage === st.stage ? '' : 'adm-dim'}>
                    <td><span class="lg-mono">{st.stage}</span> {st.label}
                      {Object.keys(st.failedByKind).length ? <span class="adm-cell-sub lg-err-line">{Object.entries(st.failedByKind).map(([k, n]) => `${k} ${n}`).join(' · ')}</span> : null}</td>
                    <td class="num">{st.queued}</td><td class="num">{st.running}</td><td class="num">{st.waiting}</td><td class="num">{st.done}</td>
                    <td class="num">{st.failed ? <b class="lg-err-line">{st.failed}</b> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div class="stack">
          <span class="eyebrow">Spend by vendor (estimate vs actual)</span>
          {d.spendByVendor.length ? (
            <div class="adm-table-wrap">
              <table>
                <thead><tr><th>Vendor</th><th class="num">Estimate</th><th class="num">Reserved</th><th class="num">Settled</th><th class="num">Actual</th></tr></thead>
                <tbody>
                  {d.spendByVendor.map((v) => (
                    <tr><td>{vendorLabel(v.vendor)}</td><td class="num"><Money usd={v.estUsd} /></td><td class="num"><Money usd={v.reservedUsd} /></td><td class="num"><Money usd={v.settledUsd} /></td><td class="num"><Money usd={v.actualUsd} /></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <span class="lg-note">Nothing spent yet.</span>}
          {d.search.startedAt ? <span class="lg-note">Started {fmtDateTime(d.search.startedAt)} ({timeAgo(d.search.startedAt)}){d.search.finishedAt ? ` · finished ${fmtDateTime(d.search.finishedAt)}` : ''}</span> : null}
        </div>
      </div>
      {d.errors.length ? (
        <div class="stack" style="gap:4px">
          <span class="eyebrow">Errors, newest first</span>
          <ul class="adm-problems">{d.errors.slice(0, 20).map((e) => <li class="error">{e}</li>)}</ul>
          {d.errors.length > 20 ? <span class="lg-note">…and {d.errors.length - 20} more.</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function RaiseBudgetModal(props: { search: LeadSearch; onClose: () => void; onDone: () => void }) {
  const s = props.search;
  const [text, setText] = useState((Math.ceil(s.budgetUsd * 1.5 * 100) / 100).toFixed(2));
  const [resume, setResume] = useState(s.status === 'paused_budget');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const next = Number(text);
  const problem = raiseProblem(s.budgetUsd, next, s.spentUsd);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      await leadgenApi.patchSearch(s.id, { budgetUsd: next });
      if (resume) await leadgenApi.resume(s.id);
      toast(`Search #${s.id} budget is now ${usd2(next)}${resume ? ' and it has resumed' : ''}.`, 'ok');
      props.onDone(); props.onClose();
    } catch (e) { setError(apiErrorView(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Raise the budget of search #${s.id}`} onClose={props.onClose}>
      <div class="stack">
        <div class="adm-kv"><dt>Budget now</dt><dd>{usd2(s.budgetUsd)}</dd><dt>Spent or reserved</dt><dd>{usd(s.spentUsd)}</dd>
          {s.estimate ? <><dt>Estimate</dt><dd>{usd(s.estimate.totalUsd)}</dd></> : null}</div>
        <Field label="New budget (USD)" hint="The month cap still applies."><input type="number" min={0.01} step={0.01} value={text} onInput={(e) => setText(inputValue(e))} /></Field>
        {s.status === 'paused_budget' ? <Check checked={resume} onChange={setResume} label="Resume the search after raising" /> : null}
        {problem && text ? <div class="lg-err-line">{problem}</div> : null}
        <ApiErrorBox error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose} disabled={busy}>Cancel</button>
          <button class="btn btn-primary" onClick={go} disabled={busy || !!problem}>{busy ? 'Saving…' : `Set ${usd2(Number.isFinite(next) ? next : 0)}`}</button>
        </div>
      </div>
    </Modal>
  );
}
